import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowUpRight, FiChevronDown } from 'react-icons/fi';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { scrollToSection } from '../../scroll/scroll';
import './Navigation.css';

gsap.registerPlugin(ScrollTrigger);

const SECTIONS = [
  ['projects', 'Projects'],
  ['experience', 'Experience'],
  ['skills', 'Skills'],
  ['education', 'Education'],
  ['contact', 'Contact'],
];

const MOBILE = '(max-width: 991.98px)';

function useMedia(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export default function Navigation({ activeSection }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [indicator, setIndicator] = useState(null);
  const isMobile = useMedia(MOBILE);
  const progressRef = useRef(null);
  const listRef = useRef(null);
  const moreRef = useRef(null);
  const menuOpenRef = useRef(menuOpen);
  const navigate = useNavigate();

  menuOpenRef.current = menuOpen;

  // Tuck the bar away while reading down the page, bring it back the moment you scroll up
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      if (Math.abs(y - lastY) < 6) return;
      const goingDown = y > lastY;
      const moreOpen = moreRef.current?.open;
      setHidden(goingDown && y > 360 && !menuOpenRef.current && !moreOpen);
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const context = gsap.context(() => {
      gsap.fromTo(progressRef.current, { scaleX: 0 }, {
        scaleX: 1,
        ease: 'none',
        scrollTrigger: { trigger: document.documentElement, start: 'top top', end: 'bottom bottom', scrub: true },
      });
    });
    return () => context.revert();
  }, []);

  // The underline slides to whichever link matches the section on screen
  const placeIndicator = useCallback(() => {
    const link = listRef.current?.querySelector('.nav-link.active');
    if (!link || window.matchMedia(MOBILE).matches) { setIndicator(null); return; }
    const inset = parseFloat(getComputedStyle(link).paddingLeft) || 0;
    setIndicator({ x: link.offsetLeft + inset, w: link.offsetWidth - inset * 2 });
  }, []);

  useLayoutEffect(placeIndicator, [activeSection, isMobile, placeIndicator]);

  useEffect(() => {
    window.addEventListener('resize', placeIndicator);
    document.fonts?.ready.then(placeIndicator);
    return () => window.removeEventListener('resize', placeIndicator);
  }, [placeIndicator]);

  // Escape or a click elsewhere closes the menus
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (moreRef.current?.open) moreRef.current.open = false;
      setMenuOpen(false);
    };
    const onPointer = (e) => {
      if (moreRef.current?.open && !moreRef.current.contains(e.target)) moreRef.current.open = false;
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, []);

  useEffect(() => { if (!isMobile) setMenuOpen(false); }, [isMobile]);

  const closeMenus = () => {
    setMenuOpen(false);
    if (moreRef.current) moreRef.current.open = false;
  };

  const jump = (event, id) => {
    event.preventDefault();
    closeMenus();
    if (window.location.hash === `#${id}`) scrollToSection(id);
    else navigate({ pathname: '/', hash: `#${id}` });
  };

  const classes = ['site-nav', 'sticky-top', scrolled && 'is-scrolled', hidden && 'is-hidden', menuOpen && 'menu-open']
    .filter(Boolean).join(' ');

  return (
    <header className={classes}>
      <nav className="navbar navbar-expand-lg" aria-label="Main navigation">
        <div className="container nav-inner">
          <a className="navbar-brand" href="#Home" onClick={(e) => jump(e, 'Home')} aria-label="Diarmuid Hession, home">
            <img src="/images/logo-96.png" alt="" className="navLogo" width="36" height="36" />
            <span className="brand-name">Diarmuid Hession</span>
          </a>
          <button
            className={`nav-toggler ${menuOpen ? 'open' : ''}`}
            type="button"
            aria-controls="navbarNav"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="toggler-bar" /><span className="toggler-bar" /><span className="toggler-bar" />
          </button>
          <div className={`navbar-collapse nav-panel ${menuOpen ? 'is-open' : ''}`} id="navbarNav" inert={isMobile && !menuOpen ? true : undefined}>
            <div className="nav-panel-inner">
              <ul ref={listRef} className="navbar-nav ms-auto align-items-lg-center">
                {SECTIONS.map(([id, label], i) => (
                  <li className="nav-item" key={id} style={{ '--i': i }}>
                    <a href={`#${id}`} className={`nav-link ${activeSection === id ? 'active' : ''}`}
                      aria-current={activeSection === id ? 'location' : undefined}
                      onClick={(e) => jump(e, id)}>{label}</a>
                  </li>
                ))}
                <li className="nav-item nav-more-item" style={{ '--i': SECTIONS.length }}>
                  <details className="nav-more" ref={moreRef}>
                    <summary>More <FiChevronDown className="nav-more-chevron" aria-hidden="true" /></summary>
                    <div className="nav-more-menu">
                      <a href="#about" onClick={(e) => jump(e, 'about')}>About</a>
                      <a href="#certificates" onClick={(e) => jump(e, 'certificates')}>Certificates</a>
                      <Link to="/weather" onClick={closeMenus}>Weather Pulse</Link>
                      <Link to="/chat" onClick={closeMenus}>Chat assistant</Link>
                    </div>
                  </details>
                </li>
                <li className="nav-item" style={{ '--i': SECTIONS.length + 1 }}>
                  <Link to="/cv" className="nav-cv" onClick={closeMenus}>CV <FiArrowUpRight aria-hidden="true" /></Link>
                </li>
                <li
                  className="nav-indicator"
                  role="presentation"
                  aria-hidden="true"
                  style={indicator ? { transform: `translateX(${indicator.x}px)`, width: indicator.w, opacity: 1 } : undefined}
                />
              </ul>
            </div>
          </div>
        </div>
      </nav>
      <span ref={progressRef} className="nav-progress" aria-hidden="true" />
    </header>
  );
}
